import { audioManager } from './audioManager';

export type SfxNavn =
  | 'cha-ching'
  | 'pageturn'
  | 'bell'
  | 'fanfare'
  | 'click'
  | 'notify';

const SFX_PATHS: Record<SfxNavn, string> = {
  'cha-ching': 'assets/audio/sfx/cha-ching.mp3',
  pageturn: 'assets/audio/sfx/pageturn.mp3',
  bell: 'assets/audio/sfx/bell.mp3',
  fanfare: 'assets/audio/sfx/fanfare.mp3',
  click: 'assets/audio/sfx/click.mp3',
  notify: 'assets/audio/sfx/notify.mp3',
};

const VOLUM_STANDARD = 0.3;
// Volum-defaults justert i runde 38 etter persona-test:
// - click ned fra 0.18 -> 0.10 (hover trigges ofte, må være subtilt)
// - pageturn ned 0.22 -> 0.18 (modal-åpne)
// - notify holdt 0.28
// - bell holdt 0.30
// - cha-ching holdt 0.32
// - fanfare holdt 0.40
const VOLUM_PER_NAVN: Partial<Record<SfxNavn, number>> = {
  click: 0.1,
  pageturn: 0.18,
  notify: 0.28,
  bell: 0.3,
  'cha-ching': 0.32,
  fanfare: 0.4,
};

const SAMME_LYD_DEMP_MS = 60;
// Fanfaren har en kort innledning som ofte klipper hvis lyden startes
// midt i en visuell overgang — vi venter litt på første avspilling.
const FANFARE_FORSTE_GANG_DELAY_MS = 100;

export class SfxManager {
  private cache: Map<SfxNavn, HTMLAudioElement> = new Map();
  private mangler: Set<SfxNavn> = new Set();
  private sisteAvspilling: Map<SfxNavn, number> = new Map();
  private aktiv = true;
  private fanfareSpiltFør = false;

  /**
   * Spiller en SFX umiddelbart. Respekterer mute, demp-vindu og disable.
   * Fanfaren får en 100ms delay første gang for å unngå klippet innledning.
   */
  play(navn: SfxNavn): void {
    if (navn === 'fanfare' && !this.fanfareSpiltFør) {
      this.fanfareSpiltFør = true;
      this.playForsinket(navn, FANFARE_FORSTE_GANG_DELAY_MS);
      return;
    }
    this.playInternt(navn);
  }

  /**
   * Spiller en SFX etter en forsinkelse (ms). Brukes for tier-up-kombo
   * der fanfare og cha-ching skal følge etter hverandre.
   */
  playForsinket(navn: SfxNavn, delayMs: number): void {
    if (delayMs <= 0) {
      this.playInternt(navn);
      return;
    }
    if (typeof setTimeout === 'undefined') return;
    setTimeout(() => this.playInternt(navn), delayMs);
  }

  /**
   * Spiller en sekvens av SFX-er med faste mellomrom.
   * Eks: playSekvens([{navn:'fanfare',delay:0},{navn:'cha-ching',delay:500}])
   */
  playSekvens(steg: Array<{ navn: SfxNavn; delay: number }>): void {
    for (const s of steg) {
      this.playForsinket(s.navn, s.delay);
    }
  }

  /**
   * Returnerer true hvis SFX vil bli avspilt nå (ikke mutet, ikke disabled).
   * Brukes av tester og UI som vil vite om lyden er aktiv.
   */
  erAktiv(): boolean {
    if (!this.aktiv) return false;
    if (audioManager.erAv) return false;
    return true;
  }

  disable(): void {
    this.aktiv = false;
  }

  enable(): void {
    this.aktiv = true;
  }

  preload(): void {
    if (typeof Audio === 'undefined') return;
    for (const navn of Object.keys(SFX_PATHS) as SfxNavn[]) {
      this.last(navn);
    }
  }

  private playInternt(navn: SfxNavn): void {
    if (!this.aktiv) return;
    if (audioManager.erAv) return;
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    if (this.mangler.has(navn)) return;

    const naa = Date.now();
    const sist = this.sisteAvspilling.get(navn) ?? 0;
    if (naa - sist < SAMME_LYD_DEMP_MS) return;
    this.sisteAvspilling.set(navn, naa);

    try {
      const original = this.last(navn);
      if (!original) return;
      const klone = original.cloneNode(true) as HTMLAudioElement;
      klone.volume = VOLUM_PER_NAVN[navn] ?? VOLUM_STANDARD;
      const result = klone.play();
      if (result && typeof result.catch === 'function') {
        result.catch(() => {
          // browser-policy eller manglende fil — feil stille
        });
      }
    } catch {
      // feil stille
    }
  }

  private last(navn: SfxNavn): HTMLAudioElement | undefined {
    const eksisterende = this.cache.get(navn);
    if (eksisterende) return eksisterende;
    if (this.mangler.has(navn)) return undefined;
    try {
      const audio = new Audio(SFX_PATHS[navn]);
      audio.preload = 'auto';
      audio.volume = VOLUM_PER_NAVN[navn] ?? VOLUM_STANDARD;
      audio.addEventListener('error', () => {
        this.mangler.add(navn);
        this.cache.delete(navn);
      });
      this.cache.set(navn, audio);
      return audio;
    } catch {
      this.mangler.add(navn);
      return undefined;
    }
  }
}

export const sfx = new SfxManager();
